import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Modal,
  Layout,
  Text,
  Padding,
  AnchorLink,
} from "@kidslivesafe/sc-react";
import { selectMemberData } from "../../features/authentication/authenticationSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  createCustomerRedisTokenAsync,
  sendMemberEventAsync,
} from "../../features/admin/adminSlice";
import { selectBrandInfo } from "../../features/brand/brandSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { formatPhoneNumber } from "../../utils";
import {
  getSubscriptionDetailsAsync,
  retryPaymentAsync,
  selectPrimarySubscription,
} from "../../features/subscriptions/subscriptionSlice";

const RetryModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pageURL = location.pathname;
  const brandInfo = useAppSelector(selectBrandInfo);
  const subscriptionDetails = useAppSelector(selectPrimarySubscription);
  const { cardType, lastFour } = useAppSelector(selectMemberData);
  const [retryClicked, setRetryClicked] = useState<boolean>(false);
  const [retrySuccess, setRetrySuccess] = useState<boolean>(false);
  const [showRetryModal, setShowRetryModal] = useState<boolean>(false);
  const [retryLoading, setRetryLoading] = useState<boolean>(false);

  const hasSentModalDisplayEvent = useRef(false);

  useEffect(() => {
    if (subscriptionDetails?.isInRebill) {
      if (!hasSentModalDisplayEvent.current && !retryClicked && !retrySuccess) {
        dispatch(
          sendMemberEventAsync({
            EventCategory: "Modal Display",
            EventAction: "Retry",
            MetaData: JSON.stringify({
              PageName: pageURL,
              Flow: "retry",
            }),
          }),
        );
        hasSentModalDisplayEvent.current = true;
      }
      setShowRetryModal(true);
    } else {
      setShowRetryModal(false);
    }
  }, [subscriptionDetails?.isInRebill]);

  const handleRetryPayment = async () => {
    setRetryLoading(true);
    try {
      let email;
      let IgnoreBillingDateCheck;
      const resultAction = await dispatch(
        retryPaymentAsync({ email, IgnoreBillingDateCheck }),
      );
      if (retryPaymentAsync.fulfilled.match(resultAction)) {
        setRetryClicked(true);
        setRetrySuccess(true);
        setRetryLoading(false);
        // get subscription details
        await dispatch(getSubscriptionDetailsAsync());

        setTimeout(() => {
          navigate("/");
        }, 0);
      } else {
        setRetryClicked(true);
        setRetrySuccess(false);
        setRetryLoading(false);
      }
    } catch (error) {
      console.error("Retry failed:", error);
    }
  };

  const handleExternalNavigation = async () => {
    const createCustomerRedisTokenAsyncResponse = await dispatch(
      createCustomerRedisTokenAsync(),
    );

    dispatch(
      sendMemberEventAsync({
        EventCategory: "Button Click",
        EventAction: "Update CC",
        MetaData: JSON.stringify({
          PageName: pageURL,
          Flow: "retry",
        }),
      }),
    );

    if (
      createCustomerRedisTokenAsync.fulfilled.match(
        createCustomerRedisTokenAsyncResponse,
      )
    ) {
      const uuid = createCustomerRedisTokenAsyncResponse.payload.token;
      window.location.href = brandInfo.homeUrl + "/fix-payment?uuid=" + uuid;
    }
  };
  return (
    <>
      <Modal
        open={showRetryModal}
        setOpen={() => {
          setShowRetryModal(false);
        }}
        size="base"
        xClose={true}
        outSideClose={true}
        scrollable={false}
      >
        <Padding
          space="sm"
          top="lg"
          breakpointStyles={{ mobile: { space: "none" } }}
        >
          <Layout
            type="flex"
            flexProps={{
              direction: "column",
              gap: "md",
              alignItems: "center",
            }}
            breakpointStyles={{
              desktop: {
                type: "flex",
                flexProps: {
                  direction: "column",
                  gap: "xl",
                  alignItems: "center",
                },
              },
            }}
          >
            {!retryClicked &&
            !retrySuccess &&
            subscriptionDetails?.isInRebill ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "sm",
                  }}
                  breakpointStyles={{
                    desktop: {
                      type: "flex",
                      flexProps: {
                        direction: "column",
                        gap: "xl",
                        alignItems: "center",
                      },
                    },
                  }}
                >
                  <div>
                    <Text textAlign="center" tag="h2">
                      Attention Needed!
                    </Text>
                  </div>
                  <Text textAlign="center" size="sm">
                    We couldn’t process your{" "}
                    <Text tag="b">
                      {cardType} card ending in {lastFour},{" "}
                    </Text>
                    which has temporarily limited your account&nbsp;access.
                  </Text>
                  <Text textAlign="center" size="sm">
                    To avoid any interruptions to your subscription, please
                    update your payment details or retry your card on&nbsp;file.
                  </Text>
                </Layout>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "md",
                    alignItems: "center",
                  }}
                  fillHorizontal
                >
                  <Button
                    buttonText="Retry Payment"
                    fill="default"
                    handleClick={handleRetryPayment}
                    clicked={retryClicked}
                    loading={retryLoading}
                  />
                  <AnchorLink
                    handleClick={handleExternalNavigation}
                    stayUnderline
                    role="button"
                  >
                    Update Payment Method
                  </AnchorLink>
                </Layout>

                <Text textAlign="center" size="sm">
                  For questions please contact member support at{" "}
                  <AnchorLink
                    inline
                    phoneNumber
                    hoverUnderline
                    href={brandInfo.phoneNumber}
                  >
                    {formatPhoneNumber(brandInfo.phoneNumber)}
                  </AnchorLink>
                  .
                </Text>
              </>
            ) : retryClicked && !retrySuccess ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "sm",
                  }}
                >
                  <div>
                    <Text textAlign="center" tag="h2" fontWeight="semibold">
                      We've encountered a problem!{" "}
                    </Text>
                  </div>
                  <Text textAlign="center" size="sm">
                    We couldn’t process your payment, possibly due to an expired
                    or declined card.
                  </Text>
                  <Text textAlign="center" size="sm">
                    Please update your payment details to maintain access to
                    your full subscription.
                  </Text>
                </Layout>
                <Button
                  buttonText="Update Payment Method"
                  handleClick={handleExternalNavigation}
                  size="md"
                  fill="fill"
                />
                <Text textAlign="center" size="sm">
                  For questions please contact member support at{" "}
                  <AnchorLink
                    inline
                    phoneNumber
                    hoverUnderline
                    href={brandInfo.phoneNumber}
                  >
                    {formatPhoneNumber(brandInfo.phoneNumber)}
                  </AnchorLink>
                  .
                </Text>
              </>
            ) : retryClicked && retrySuccess && showRetryModal ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "lg",
                  }}
                >
                  <div>
                    <Text textAlign="center" tag="h2" fontWeight="semibold">
                      Success!{" "}
                    </Text>
                    <Text textAlign="center" size="sm">
                      We were able to successfully process your payment.
                    </Text>
                  </div>
                </Layout>
                <Button
                  buttonText="Go to Dashboard"
                  handleClick={() => {
                    setShowRetryModal(false);
                  }}
                  color="success"
                  cursor="pointer"
                />
              </>
            ) : null}
          </Layout>
        </Padding>
      </Modal>
    </>
  );
};
export default RetryModal;
